import { TRIP_PURPOSES, TRIP_PURPOSE_LABELS, type TripDraft, studentsValue } from './tripDraft.js';

/**
 * 361 (#980): "Your trip", the questions a customer answers on their booking (approved design, round 2). The number is
 * typed (the PO's rule: a list would have everyone pick 1), the purpose is one tap, food delivery is Yes/No, and the
 * non-halal question appears only after a Yes. Each choice is a real button with `aria-pressed`, named in full for a
 * screen reader ("Yes, OK to do food delivery"). The form decides what is missing and holds Continue.
 */
interface TripStepProps {
  value: TripDraft;
  onChange: (next: TripDraft) => void;
}

const CHIP = 'h-9 rounded-full border px-3.5 text-sm font-semibold transition-colors';
const CHIP_ON = 'border-primary bg-primary text-on-primary';
const CHIP_OFF = 'border-border text-text hover:border-border-light';
const CHOICE = 'h-11 rounded-full border text-sm font-semibold transition-colors';

function YesNo({
  value,
  onChange,
  yesName,
  noName,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  yesName: string;
  noName: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          aria-pressed={value === v}
          aria-label={v ? yesName : noName}
          onClick={() => onChange(v)}
          className={`${CHOICE} ${value === v ? CHIP_ON : CHIP_OFF}`}
        >
          {v ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  );
}

export function TripStep({ value, onChange }: TripStepProps) {
  const set = (patch: Partial<TripDraft>) => onChange({ ...value, ...patch });
  const studentsInvalid = value.students.trim() !== '' && studentsValue(value.students) === null;
  return (
    <section className="flex flex-col gap-3" aria-labelledby="trip-heading">
      <div className="flex flex-col gap-1">
        <h2 id="trip-heading" className="text-base font-bold text-text">
          Your trip
        </h2>
        <p className="text-sm text-text-muted">Helps us plan better. Takes a few seconds.</p>
      </div>
      <div className="bg-surface-card flex flex-col gap-4 rounded-xl p-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="trip-students" className="text-sm text-text-muted">
            How many students will be in the car?
          </label>
          <div
            className={`bg-surface-mid flex items-center rounded-lg border px-4 ${studentsInvalid ? 'border-error' : 'border-border focus-within:border-primary'}`}
          >
            <input
              id="trip-students"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={2}
              value={value.students}
              aria-invalid={studentsInvalid}
              aria-describedby={studentsInvalid ? 'trip-students-error' : undefined}
              onInput={(e) => set({ students: (e.target as HTMLInputElement).value })}
              className="min-w-0 flex-1 bg-transparent py-3 text-text outline-none"
            />
            <span className="text-xs text-text-muted" aria-hidden="true">
              1–5
            </span>
          </div>
          {studentsInvalid && (
            <p id="trip-students-error" className="text-xs text-error">
              Enter a whole number from 1 to 5.
            </p>
          )}
        </div>

        <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 p-0 text-sm text-text-muted">What's the trip for?</legend>
          <div className="flex flex-wrap gap-2">
            {TRIP_PURPOSES.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={value.purpose === p}
                onClick={() => set({ purpose: p })}
                className={`${CHIP} ${value.purpose === p ? CHIP_ON : CHIP_OFF}`}
              >
                {TRIP_PURPOSE_LABELS[p]}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <p className="text-sm text-text-muted">Are you OK to do food delivery?</p>
          <YesNo
            value={value.foodDelivery}
            // A changed mind to No drops the non-halal answer: that question is no longer asked.
            onChange={(v) => set({ foodDelivery: v, nonHalal: v ? value.nonHalal : null })}
            yesName="Yes, OK to do food delivery"
            noName="No, not OK to do food delivery"
          />
        </div>

        {value.foodDelivery === true && (
          <div className="bg-surface-alt flex flex-col gap-2 rounded-lg p-3">
            <p className="text-sm text-text-muted">Are you OK with non-halal food?</p>
            <YesNo
              value={value.nonHalal}
              onChange={(v) => set({ nonHalal: v })}
              yesName="Yes, OK with non-halal food"
              noName="No, not OK with non-halal food"
            />
          </div>
        )}
      </div>
    </section>
  );
}
